/**
 * The repository interface — docs/ARCHITECTURE.md §5.1.
 *
 * One interface, three implementations: `SupabaseRepository` (Phase 4),
 * `LocalRepository` (here, Phase 2b) and a test `MemoryRepository` (which is
 * just `LocalRepository` over an in-memory `StorageLike`). Method names mirror
 * the prototype's methods so the store actions are 1:1 with `renderVals()`'s
 * call sites.
 *
 * DEVIATIONS from §5.1, all small and all deliberate:
 *
 *  - P2-02  Rows are addressed by `EntryId`/`PlaytestId` strings, and
 *           `ListEntry` carries both `id` (the game id, what the ported logic
 *           keys on) and `entryId` (the repository handle). §5.1 already typed
 *           `removeFromList(entryId: string)`; this just makes the round trip
 *           explicit on the way *out* as well.
 *  - P2-03  `setIntAt` / `placeTier` take a **target index** rather than a
 *           `beforeAppid`. The prototype's drag machine produces an index
 *           (`dragMove` line 1341), and LocalRepository stores whole order
 *           arrays. SupabaseRepository converts index → midpoint position when
 *           it lands; the conversion is trivial and belongs on that side.
 *  - P2-04  `onPickAv` takes a data URL, not a `Blob`. LocalRepository has no
 *           object storage, and the prototype already produced a data URL from
 *           its canvas crop. The Supabase implementation accepts either.
 *  - P4-02  `CrewHandlers` gained three OPTIONAL members (`onPatch`, `onEvent`,
 *           `onStatus` was already there) so the Supabase implementation can
 *           satisfy ARCHITECTURE §2.2's "patch is the default" and §2.4's
 *           attributed toasts. `LocalRepository` implements none of them and is
 *           unchanged; consumers that supply none get the old behaviour.
 */

import type { RecoveryLink } from '../lib/joinLink.ts';
import type { CcuSample, PriceSample } from '../lib/logic.ts';

/**
 * Detail-only `steam_facts` columns. Every field is optional-by-absence rather
 * than defaulted, because for each of them "we have not asked" and "we asked
 * and there is nothing" are DIFFERENT screen states and a default would erase
 * the distinction.
 */
export interface DetailFacts {
  /** Newest patchnotes-tagged Steam announcement, ISO-8601, or null. */
  patchAt: string | null;
  patchTitle: string | null;
  /** When op=patch last ASKED. Null = never checked; this is what separates
   *  "no patch notes on Steam" from "the job has not reached this row". */
  patchSeenAt: string | null;
  /** Percent positive over the trailing 30 days, or null for a quiet window. */
  rev30Pct: number | null;
  /** Steam's own 1..9 rung FOR THAT WINDOW — not derivable from the percentage
   *  (migration 3040's argument, which bites harder over 30 days). */
  rev30Score: number | null;
  /** How many reviews the window held. 0 is a measurement. */
  rev30Ct: number;
  /** When op=reviews30 last asked. Null = never checked. */
  rev30At: string | null;
}
import type { RoundSnapshot } from '../lib/round.ts';
import type { SchedMap, SchedNom } from '../lib/schedule.ts';
import type { VideoDraft } from '../lib/videoUrl.ts';
import type {
  AssetMap,
  AuthSession,
  BacklogEntry,
  BoardEvent,
  CrewScoreRow,
  SendRec,
  SendStatus,
  WatchRec,
  Comment,
  CrewEvent,
  EntryId,
  Filters,
  Game,
  GameId,
  GameMedia,
  GameVideo,
  Invite,
  ListEntry,
  PlayersSrc,
  Playtest,
  PlaytestStatus,
  Profile,
  PtBrowseRow,
  PtCheckResult,
  PtOffInput,
  PtSteamInput,
  RosterEntry,
  SortMode,
  SteamFactPatch,
  SteamLink,
  SyncResult,
  Tier,
  Unsub,
  User,
  VoteValue,
} from '../lib/types.ts';

/**
 * One member's Steam-LINK state — migration 3010, and the Friends roster's dot.
 *
 * Its own type rather than a field on `User` because it is a different read with
 * a different lifetime: `users` comes off the roster snapshot, this comes off a
 * SECURITY DEFINER carve-out that only an authenticated member of the crew may
 * call, and conflating them would put a privileged answer on an object that a
 * dozen selectors treat as freely available.
 */
export interface CrewLinkRow {
  userKey: string;
  linked: boolean;
}

/**
 * One member's library state for one game.
 *
 * Two booleans rather than a tri-state string because the two questions are
 * independent: `linked` is about the member (have they connected Steam at all),
 * `owned` is about the game. A single enum would invite a default, and the
 * default would be a claim.
 *
 * EXTENDS `CrewLinkRow` because `linked` here IS that same fact, asked with a
 * game in hand — 3010 is 3007's first column without the appid, not a second
 * source for it. If the two ever disagree, one of the two RPCs is wrong.
 */
export interface CrewOwnershipRow extends CrewLinkRow {
  owned: boolean;
}

/**
 * Who set a game's player-count override, and when (migration 3008).
 *
 * `byKey` is the app's display handle and is `''` when the setter is not in the
 * caller's crew — the override is GLOBAL while `profiles` is crew-scoped, so
 * that is a state the UI has to render rather than a lookup failure. `byKnown`
 * separates it from the other empty: `false` means the setter's profile is gone
 * (`ovr_by` is `on delete set null`). Collapsing the two would report a former
 * crewmate's correction as a stranger's.
 */
export interface PlayerOverride {
  min: number;
  max: number;
  /** Epoch ms. */
  at: number;
  /** `''` when the setter is outside the caller's crew — see `byKnown`. */
  byKey: string;
  byKnown: boolean;
}

/**
 * A row's player count after the resolver has run, as `v_games` reports it.
 * `PlayersSrc` is re-exported through `lib/types.ts`; imported here rather than
 * redeclared so the five wire values have exactly one spelling.
 */
export interface ResolvedPlayers {
  mn: number;
  mx: number;
  punb: boolean;
  psrc?: PlayersSrc;
}

/**
 * Every way `set_player_count` can refuse, normalised off the raised message.
 *
 * `FAILED` is the catch-all and is deliberately NOT named `INTERNAL`: that
 * string is in `mutate.ts`'s `NETWORK_CODES`, and a write that cannot succeed
 * must never land somewhere that will silently retry it.
 */
export type SetPlayerCountError =
  | 'NOT_AUTHENTICATED'
  | 'MUST_RESET'
  | 'RATE_LIMITED'
  | 'UNKNOWN_GAME'
  | 'BAD_RANGE'
  | 'BAD_NOTE'
  | 'FAILED';

/**
 * The six the function actually raises, in ITS evaluation order (2906) — which
 * is also match order, so the first one found is the one that fired.
 */
export const SET_PLAYER_COUNT_ERRORS = [
  'NOT_AUTHENTICATED',
  'MUST_RESET',
  'RATE_LIMITED',
  'UNKNOWN_GAME',
  'BAD_RANGE',
  'BAD_NOTE',
] as const satisfies readonly SetPlayerCountError[];

/**
 * The answer to "I invited them" (3026, docs/INVITES.md).
 *
 * `link` is present ONLY when the email could not be sent — no provider
 * configured for the function, or a refusal from it. A copied link is bearer
 * authority, so it is not minted on the path where the message went out; the
 * surface shows one panel or the other on exactly this field.
 */
export interface InviteSent {
  email: string;
  delivery: 'email' | 'link';
  expiresAt: string;
  link?: string;
}

/** An open invitation, as the owner's PENDING list sees it. Never a token. */
export interface PendingInvite {
  email: string;
  invitedAt: string;
  expiresAt: string;
  /** How it was delivered, and whether that actually happened. */
  delivery: 'email' | 'link';
  sent: boolean;
}

/** What the JOIN screen can know before anybody has agreed to anything. */
export interface InvitePeek {
  state:
    | 'live'
    | 'expired'
    | 'accepted'
    | 'declined'
    | 'revoked'
    | 'superseded'
    | 'invalid';
  crewName: string;
  crewSlug: string;
  /** Display name, never an address — not even the inviter's. */
  inviterName: string;
  /** `ch****@example.com`: enough to recognise, not enough to publish. */
  emailMasked: string;
  expiresAt: string;
}

/* -- join links (3056, docs/JOIN-LINKS.md) --------------------------------- */

/** The leader's view of their party's one live link. NEVER carries a token:
 *  only the hash is stored, so a link can be minted and copied once and never
 *  shown again. Rotating is the only way to get a URL back. */
export interface JoinLinkState {
  live: boolean;
  createdAt: string | null;
  /**
   * The link, ready to copy — or null.
   *
   * READABLE FROM 3057, and deliberately not the posture `crew_invites` takes.
   * An invite token is admission and is emailed once; a join-link token only
   * buys the right to ask, and the leader still approves. A link meant to be
   * re-shared into a group chat has to be re-readable, or the only way to
   * share it again is to mint a new one and kill the copy already out there.
   *
   * Null for links minted before 3057 — nothing was stored, so there is
   * genuinely nothing to show and the surface says so.
   */
  joinUrl: string | null;
}

/** What `newPartyJoinLink` returns — the single moment the URL exists outside
 *  the leader's clipboard. */
export interface JoinLinkMinted {
  live: boolean;
  joinUrl: string;
}

/** One open request, as the leader's REQUESTS list sees it.
 *
 *  DISPLAY NAME AND NOTHING ELSE ABOUT THE PERSON — no address. The roster has
 *  never shown one and a request list is a worse place to start. */
export interface JoinRequest {
  id: string;
  displayName: string;
  requestedAt: string;
}

/** What the ASK screen can know before anybody has proven anything. */
export interface JoinLinkPeek {
  state: 'live' | 'invalid';
  crewName: string;
  crewSlug: string;
  /** Display name, never an address. Naming the leader is the difference
   *  between a join link and a phishing page. */
  leaderName: string;
  /** They are already on this party's roster, so the screen offers to open it
   *  rather than inviting them to ask for what they have. */
  alreadyMember: boolean;
}

/** `start` succeeded: a code is in flight. Creates no account. */
export interface JoinCodeSent {
  /** `ma****@example.com` — enough to know which mailbox to check. */
  emailMasked: string;
  expiresAt: string;
  expiresMinutes: number;
}

/** `verify` succeeded: the address is proven, the account exists, the ask is
 *  recorded. Still NOT a membership — the leader decides that. */
export interface JoinAsked {
  createdAccount: boolean;
  crewName: string;
  /**
   * The address the new account signs in with. Present only when an account was
   * created, and the client CANNOT derive it: every other account in the
   * product logs in at a name-derived address and this one does not.
   * `invite-accept` learned that the hard way (2026-08-21).
   */
  email?: string;
}

/** Am I waiting on a leader, and which one? */
export interface JoinStatus {
  waiting: boolean;
  crewName: string;
  crewSlug: string;
  leaderName: string;
  requestedAt: string;
}

export interface InviteAccepted {
  /** False when an existing account joined a second crew. */
  createdAccount: boolean;
  /**
   * The address the new account signs in with — the one the invitation was
   * sent to. Present only when an account was created, and the client CANNOT
   * derive it: every other account in the product logs in at a name-derived
   * address, and an invited one does not.
   */
  email?: string;
  crewId: string;
  crewName: string;
  userKey?: string;
}

/**
 * One of my crews, as `public.my_crews()` returns it (migration 3015, D2).
 * The switcher menu and the crew-count line both read this.
 */
export interface CrewMembership {
  crewId: string;
  slug: string;
  name: string;
  /** My role IN THIS CREW — not a property of the account (D4). */
  role: 'owner' | 'player';
  memberCount: number;
}

/**
 * The crew a snapshot was actually scoped to (D2).
 *
 * The server decides which crew a read returns — `my_crew_id()` resolves it,
 * RLS applies it — so the client can be wrong about which crew it is looking
 * at without anything erroring: a crew deleted under a live session, or a
 * member removed from their active crew, both leave the helper quietly falling
 * back to another membership while the header still names the dead one.
 * Returning the identity WITH the data is what makes that detectable, and it
 * is also the only place the crew's real name comes from.
 */
export interface CrewIdentity {
  id: string;
  name: string;
  slug: string;
  /** Epoch ms. MANAGE CREW's meta line is the only reader. */
  createdAt: number;
}

/** Argument to `createCrew` — see the method for the two arms. */
export interface CreateCrewInput {
  name: string;
  /** Signed-out only: the owner's account, created with the crew. */
  account?: { displayName: string; email: string; password: string };
}

/** Everything the crew shares. Fetched once at boot, patched by realtime. */
export interface CrewSnapshot {
  /**
   * Which crew the rows below actually came from, or null when the account
   * has no crew at all (the phase-D landing state). Compare against the store's
   * active crew and re-boot on mismatch — see `CrewIdentity`.
   */
  crew: CrewIdentity | null;
  members: User[];
  entries: ListEntry[];
  playtests: Playtest[];
  /** ids the current member has dismissed in What's New */
  seen: GameId[];
  /** the current member's last What's New review */
  lastReviewedAt: number;
  events: CrewEvent[];
  /**
   * Crew-submitted gameplay links, ALL games at once rather than per-game
   * (migration 2200). The whole set is a few rows and the carousel has to be
   * able to render on first paint of a Detail screen, so a per-game fetch would
   * buy a round trip and a spinner for nothing.
   */
  videos: GameVideo[];
}

/** The current member's private boards (ARCHITECTURE §1.6). */
/** One year of a crewmate's GOTY list, as the player's page draws it. */
export interface MemberGotyYear {
  year: number;
  potentials: GameId[];
  tiers: Record<string, Tier>;
  tierOrder: GameId[];
}

export interface MyBoards {
  potentials: GameId[];
  tiers: Record<string, Tier>;
  tierOrder: GameId[];
  skips: GameId[];
  intOrder: GameId[];
  /**
   * The MY INTERESTS skip queue (3037, DESIGN-CHANGELOG 2026-08-24) — the
   * appids `SKIP FOR NOW` deferred, oldest deferral first. `skips` is the same
   * fact for the GOTY board; the two are separate because the boards are.
   */
  intSkips: GameId[];
}

export interface ProvisionalGame {
  appid: number;
  name: string;
  dev: string;
  releaseYm: string;
  price: number | null;
  oldPrice: number;
  upcoming: boolean;
  description: string;
  artUrl: string;
}

/** One game out of the member's Steam library, as the GOTY auto-fill sees it. */
export interface SteamFillGame {
  appid: number;
  /** Steam's own store name — `include_appinfo=1` on the `op=goty` call. */
  name: string;
  playtimeMin: number;
  /** `rtime_last_played` as epoch ms; always inside the requested year. */
  lastPlayedAt: number;
}

/**
 * Why a candidate is on the list. Four observations and one guess — the order
 * IS the confidence order, and it mirrors `_shared/gotyfill.ts#SIGNAL_ORDER`
 * exactly. The two must agree; the server ranks by it and the picker labels by
 * it.
 *
 *   played       Steam handed us a real last-played date
 *   achievement  an achievement carries its own unlock date
 *   ledger       we watched playtime grow between two syncs
 *   recent       played inside the last fortnight
 *   released     shipped that year with no trace of Early Access — INFERRED
 */
export type GotySignal = 'played' | 'achievement' | 'ledger' | 'recent' | 'released';

/** One game the fallback thinks the member played, and why it thinks so. */
export interface GotyCandidate {
  appid: number;
  name: string;
  /** LIFETIME playtime — Steam exposes no per-year total. Ordering only. */
  playtimeMin: number;
  /** The strongest signal that fired. */
  signal: GotySignal;
  /** Everything that fired, strongest first, so a row can show its working. */
  signals: GotySignal[];
  /** Epoch ms of the dated evidence; 0 when the signal produced none. */
  evidenceAt: number;
  /** False only for `released`. The picker pre-ticks confident rows only. */
  confident: boolean;
}

/** One row of the member's owned library, for the picker's search list. */
export interface GotyLibraryGame {
  appid: number;
  name: string;
  playtimeMin: number;
}

/** The numbers the picker needs to explain itself without inventing a reason. */
export interface GotySignalCounts {
  owned: number;
  withPlaytime: number;
  /** THE diagnostic. Zero is why the fallback ran at all. */
  withStamp: number;
  fromPlayed: number;
  fromAchievement: number;
  fromLedger: number;
  fromRecent: number;
  fromReleased: number;
  achievementsChecked: number;
  /** True when the achievement sweep stopped early — no silent caps. */
  achievementsCapped: boolean;
}

/**
 * DEVIATION (G-04): `steamFill` used to answer `number[]`, which could not say
 * WHY it was empty. Three very different states all arrived as `[]` — a private
 * profile, a year with no playtime, and "you already have them all" — and the
 * store had no choice but to blame the last one. The result object separates
 * them, and carries names so a game the crew catalog has never seen can be
 * minted through `ensureGame` instead of being silently dropped.
 *
 * DEVIATION (G-07): a fourth state turned out to be the COMMON one. Steam
 * returns `rtime_last_played` only when the Web API key belongs to the account
 * being queried, so for every member except the key's owner `games` is not
 * merely empty, the rule behind it is unsatisfiable. `dated` says which world we
 * are in; `candidates` carries what the fallback signals could recover instead.
 */
export interface SteamFillResult {
  /** False when Steam answered but GAME DETAILS are private; `games` is empty. */
  profilePublic: boolean;
  /** Most-played first, capped server-side. Empty whenever `dated` is false. */
  games: SteamFillGame[];
  /**
   * True when Steam dated this library at all. False means the primary rule
   * could never have matched, so an empty `games` is NOT evidence the member
   * played nothing — the single most important bit in this envelope.
   */
  dated: boolean;
  /** Fallback suggestions, ranked confident-first. Empty when `dated`. */
  candidates: GotyCandidate[];
  /** The whole owned library, most-played first, for the picker's search. */
  library: GotyLibraryGame[];
  /** Null when the fallback did not run (dated, or a private profile). */
  counts: GotySignalCounts | null;
  /**
   * Where the member's full library check stands (migration 3070). ABSENT from a
   * function that predates it, and from the paths that never reach the fallback —
   * the store treats absent as "nothing to wait for", which is today's behaviour.
   */
  backfill?: SteamBackfill;
}

export type SteamBackfillStatus =
  | 'idle'
  | 'queued'
  | 'running'
  | 'complete'
  | 'failed'
  | 'blocked_private';

/**
 * The one full pass over a member's Steam library that dates their play from
 * achievements — one keyed call per game, so about a minute and a half, run by
 * the SERVER and finished whether or not this tab is still open.
 *
 * `waiting` is the only field the store branches on: true means a complete
 * answer is on its way and the picker should be held for it. It is false for
 * `failed` and `blocked_private` on purpose — those are answerable states, and
 * nobody may sit on CHECKING YOUR STEAM LIBRARY forever.
 */
export interface SteamBackfill {
  status: SteamBackfillStatus;
  done: number;
  total: number;
  waiting: boolean;
}

/** One game off the member's Steam wishlist, as the BACKLOG import sees it. */
export interface WishlistGame {
  appid: number;
  /** Steam's store name via GetItems; `''` when the server's name budget ran out. */
  name: string;
  /** `''` when Steam did not say. */
  dev: string;
  /** Coming soon on Steam — what makes the detail link read WISHLIST ON STEAM. */
  upcoming: boolean;
}

/**
 * `steam-sync op=wishlist` (docs/DISCOVERY.md §7.1a). Unkeyed server-side, so it
 * works on a deployment with no Steam key.
 */
export interface SteamWishlistResult {
  /**
   * False when Steam sent no item list at all. That is a PRIVATE wishlist or,
   * very probably, an EMPTY one — Steam cannot be told apart here — so copy
   * built on it has to allow for both.
   */
  open: boolean;
  /** In the member's order: their Steam rank, then the order they wished. */
  games: WishlistGame[];
  /** Everything on the wishlist, before `notGames`/`unresolved` were left off. */
  total: number;
  /** DLC, soundtracks, software — Steam says not a game, so not imported. */
  notGames: number;
  /** Steam would not resolve these appids (delisted, region-locked). */
  unresolved: number;
  /** In `games` with no name yet; the nightly pass names them. */
  unnamed: number;
}

export interface LiveSearchResult {
  items: Game[];
  src: 'steam' | 'cs';
  /** false when the result came from `data/fixtures` because the API was down */
  fromNetwork: boolean;
}

// ---------------------------------------------------------------------------
// Discover browse — Steam's own search (docs/DISCOVERY.md §2.2, §5)
// ---------------------------------------------------------------------------

/**
 * One page of Steam's search. `term` and `tags` may be combined; at least one
 * must be present, because a browse with neither is the whole 272k-row catalog
 * in Steam's default order, which is not a question Discover asks.
 */
export interface BrowseQuery {
  term: string;
  /** Steam tag ids, ANDed by Steam. Ids reach ~1.1e6 — never a smallint. */
  tags: number[];
  start: number;
  /**
   * The sort, in STEAM'S vocabulary, or `null` for Steam's own relevance order.
   *
   * Deliberately not our `SortMode`: the mapping is lossy in a way the caller
   * has to handle rather than hide. Steam honours five sorts and has no
   * player-count sort at all (`worker/src/steam.ts#BROWSE_SORTS` has the
   * measurement), so `peak` maps to `null` here and the screen says why. A
   * repository must not be the place that silently substitutes one ranking for
   * another.
   */
  sort: 'Released_DESC' | 'Name_ASC' | 'Price_ASC' | 'Price_DESC' | 'Reviews_DESC' | null;
  /**
   * Price cap in whole dollars, or `'free'`, or `null` for no cap.
   *
   * THERE IS NO FLOOR, and there is no field here for one. Steam's `minprice`
   * is silently ignored — measured 2026-08-23: `minprice=20&maxprice=40` and
   * `minprice=30&maxprice=40` both return the identical 11,759 rows that
   * `maxprice=40` returns alone. The price range's low bound is unanswerable in
   * browse mode from either end, since browse rows carry no price either, and
   * `discoverVM` names that gap on screen rather than leaving a slider that
   * does nothing.
   */
  maxPrice: number | 'free' | null;
  /** Discounted only (`specials=1`). */
  specials: boolean;
  /** Unreleased only (`filter=comingsoon`). */
  comingSoon: boolean;
}

/**
 * One resolved seat count from `player-lookup` (migration 3034) — the
 * live-once lookup for browsed games the pool has never heard of.
 *
 * `unb` mirrors `game_players.res_unbounded`: at or above the server's
 * sentinel the number is a CAPACITY (Factorio's 65535), not a party size, and
 * the client renders it as the band exactly as it does for pool rows.
 */
export interface SeatCount {
  appid: number;
  mn: number;
  mx: number;
  unb: boolean;
}

export interface BrowsePageResult {
  /** Steam's count of everything matching — NOT the length of `items`. */
  total: number;
  /** Browse rows as ordinary `live` games, in Steam's relevance order. */
  items: Game[];
  /**
   * Art `GetItems` already returned, keyed by appid, for merging straight into
   * the store's `assets` map.
   *
   * This is why a browse tile paints immediately and costs no extra request: the
   * hashed capsule paths came back with the row, and `artCands` prefers a
   * fetched asset over a guessed legacy URL — which for a modern appid is the
   * difference between art and a blank tile (see `lib/logic.ts#artCands`).
   */
  assets: AssetMap;
  start: number;
  /** appids the search returned before non-games were dropped. */
  found: number;
  /**
   * Steam's paging wall, not the end of the results. Lets the caller say "that
   * is as far as Steam will let us look" instead of implying there is no more.
   */
  capped: boolean;
  fromNetwork: boolean;
}

/** One row of Steam's tag vocabulary, for the tag picker. */
export interface SteamTag {
  id: number;
  name: string;
}

// ---------------------------------------------------------------------------
// Discover feed — the ranked, personalised default view (DISCOVERY.md §5.4–5.5)
// ---------------------------------------------------------------------------

/**
 * Why the feed is as personal as it is. Every value is a DIFFERENT SCREEN, not a
 * severity — §5.6's rule is that none of these may present as an empty result.
 *
 *   ok           ranked against a real taste profile
 *   no_link      no Steam account linked; popularity order, and say so
 *   private      linked but the profile is private; same, plus the existing
 *                PRIVATE explanation
 *   no_taste     linked and public, but nothing has been featured yet — the
 *                normal state on the first night
 *   empty_table  the games table itself is empty. Distinct from "your filters
 *                matched nothing", and the screen says something different.
 */
export type DiscoverReason = 'ok' | 'no_link' | 'private' | 'no_taste' | 'empty_table';

/**
 * One page of the ranked feed.
 *
 * `seed` is session-stable and participates in the sort. A sampled feed whose
 * order is not reproducible produces duplicates and gaps across pages (§5.5), so
 * the same seed must be sent for every page of one browse — and a NEW seed is
 * what makes a fresh session feel fresh.
 *
 * `cursor` is opaque: a base64 `<score>|<appid>` keyset the server built. The
 * client never parses it, and never re-sorts a returned page — the score it
 * would have to sort by is not in the payload, deliberately.
 */
export interface DiscoverQuery {
  q: string;
  seed: number;
  limit: number;
  cursor: string | null;
  sort: SortMode;
  /**
   * Sent whole. The repository owns the SENTINEL TRANSLATION, because
   * `defaultFilters()` is `pr: [0, 60]` and `passFlt` reads that as INACTIVE
   * with 60 meaning "no upper bound" — forwarding it literally would drop every
   * upcoming game and everything over $60 from the DEFAULT screen while the
   * filter button still reported zero active filters.
   */
  flt: Filters;
  /**
   * Excluded tags as `steam_tags.slug` strings — the tag picker's exclude
   * list, client-slugified from its Steam-vocabulary names by
   * `lib/format.ts#tagId`, which is character-identical to the server-side
   * slugger that generated `steam_tags.slug` in the first place. Sent as
   * `discover_page`'s `p.xtags`: the server resolves each slug back to its
   * tag id and drops a row when ANY exclude matches, in either vocabulary,
   * known-gated (a row with no tags at all passes — decision 5). Always
   * present; `[]` means none.
   */
  excludes: string[];
}

export interface DiscoverPageResult {
  rows: Game[];
  /** Art that came back with the page, for merging into the store's map. */
  assets: AssetMap;
  /**
   * The TRUE total over the whole candidate set, not the size of this window.
   * Under server paging the client cannot count it, so it has to travel with the
   * page or the match line becomes a lie about a different set (§5.5).
   */
  total: number;
  /** Ditto: what the filters removed, measured over the whole set. */
  hiddenByFilters: number;
  /** How many rows the feed could have drawn from at all. 0 ⇒ empty_table. */
  poolN: number;
  /** Tag counts over BOTH vocabularies, so the sheet and the grid agree. */
  tagCounts: Array<{ id: string; label: string; n: number }>;
  /** null when this was the last page. */
  nextCursor: string | null;
  /** The limit the server ACTUALLY used after clamping, not the one asked for. */
  limitUsed: number;
  personalized: boolean;
  reason: DiscoverReason;
  seed: number;
  /**
   * false when the RPC could not be reached. The caller degrades to the Phase 1
   * client path AND SAYS SO — §5.6 is explicit that a silent fallback is how a
   * broken feed would look identical to a working one.
   */
  fromNetwork: boolean;
}

/**
 * DEVIATION (P2-06): §5.1 typed `giFlush` as `Promise<Record<number, AssetRec>>`.
 * It reports `fromNetwork` as well, because the freshness chip is an assertion
 * about a round-trip that actually happened (§5.4) — a fixture-backed answer
 * must be able to say so, or the chip goes back to being theater.
 */
export interface AssetsResult {
  assets: AssetMap;
  fromNetwork: boolean;
}

/**
 * What one `appdetails` round trip yields.
 *
 * `enrich` used to return the `SteamFactPatch` alone. The carousel's trailers
 * and screenshots live in the SAME response, so returning both is what keeps a
 * Detail open at one request instead of two — and it keeps the split explicit:
 * `patch` is written back to `steam_facts`, `media` never is (decision D2).
 */
export interface EnrichResult {
  patch: SteamFactPatch;
  media: GameMedia;
}

/**
 * A single realtime row change, already translated out of database shapes and
 * into the domain types the store holds (ARCHITECTURE §2.2, "patch is the
 * default"). Keeping the translation on the repository side is what lets the
 * store apply a patch without knowing a column name.
 *
 * DEVIATION (P4-02): §5.1 typed `subscribeCrew` with a bare `onChange`. That is
 * all `LocalRepository` can offer — a `BroadcastChannel` message says "something
 * changed", not what — but §2.2 requires per-row patching for the real backend,
 * so the handler bag gained three OPTIONAL members. `LocalRepository` is
 * untouched and still drives `onChange`; `SupabaseRepository` prefers `onPatch`
 * when the consumer supplies it and falls back to `onChange` when it does not.
 */
export type CrewPatch =
  /** a game the local catalog has never seen (§2.1: single `v_games` fetch) */
  | { t: 'catalog.upsert'; game: Game }
  | { t: 'entry.upsert'; entry: ListEntry }
  | { t: 'entry.remove'; entryId: EntryId }
  | { t: 'vote.set'; entryId: EntryId; userKey: string; value: VoteValue | null }
  | { t: 'comment.upsert'; entryId: EntryId; comment: Comment }
  | { t: 'comment.remove'; entryId: EntryId; commentId: string }
  | { t: 'playtest.upsert'; playtest: Playtest }
  | { t: 'playtest.remove'; playtestId: string }
  | { t: 'pt.int'; playtestId: string; userKey: string; at: number | null }
  | { t: 'pt.status'; playtestId: string; userKey: string; status: PlaytestStatus | null }
  | { t: 'member.upsert'; user: User }
  | { t: 'member.remove'; userKey: string }
  | { t: 'video.upsert'; video: GameVideo }
  /**
   * `appid` rides along because a realtime DELETE is redacted to the replica
   * identity the subscriber may read, so the store cannot look the row up after
   * the fact — same reason `entryByComment` exists for comments.
   */
  | { t: 'video.remove'; videoId: string; appid: number }
  /**
   * Any row of `rounds` / `round_members` / `round_noms` moved (3039). Coarse
   * on purpose: the round is one small JSON snapshot whose parts must always
   * agree (a nomination without its round, a spin without its table, are not
   * states the page can render), so the store refetches `roundGet()` rather
   * than patching three tables into one object.
   */
  | { t: 'round.changed' }
  /**
   * Any row of `schedule_noms` / `schedule_backers` moved (3071). Coarse for
   * the same reason as the round: a night is one small JSON array whose
   * nominations and backers must agree, so the store refetches the span it
   * is showing rather than patching two tables into one map.
   */
  | { t: 'sched.changed' };

/**
 * The `mutate()` key a patch would echo, so the store can consult its in-flight
 * map (§2.3 echo suppression) without re-deriving the key shape per table.
 */
export function patchEchoKey(p: CrewPatch, meKey: string): string | null {
  switch (p.t) {
    case 'entry.upsert':
      return 'add:' + p.entry.id;
    case 'entry.remove':
      return 'rmlist:' + p.entryId;
    case 'vote.set':
      return p.userKey === meKey ? 'vote:' + p.entryId + ':' + meKey : null;
    case 'comment.upsert':
    case 'comment.remove':
      return 'comment:' + p.entryId;
    case 'playtest.remove':
      return 'ptUntrack:' + p.playtestId;
    case 'pt.int':
      return p.userKey === meKey ? 'ptInt:' + p.playtestId + ':' + meKey : null;
    case 'pt.status':
      return p.userKey === meKey ? 'ptSt:' + p.playtestId + ':' + meKey : null;
    case 'member.upsert':
      return p.user.k === meKey ? 'profile:' + meKey : 'role:' + p.user.k;
    case 'member.remove':
      return 'rm:' + p.userKey;
    // The add key is keyed by (appid, video key) rather than by row id: the
    // optimistic row has no server id yet, so the id could not match.
    case 'video.upsert':
      return 'video:' + p.video.appid + ':' + p.video.provider + ':' + p.video.key;
    case 'video.remove':
      return 'video:rm:' + p.videoId;
    default:
      return null;
  }
}

/** Realtime handlers. LocalRepository drives these from `BroadcastChannel`. */
export interface CrewHandlers {
  /** a peer committed a write; `actorKey` is the writer, for toast attribution */
  onChange: (ev: { table: string; actorKey: string }) => void;
  /** §2.2 per-row patch. When present, `SupabaseRepository` uses it instead of `onChange`. */
  onPatch?: (p: CrewPatch) => void;
  /** §2.4 attributed toasts. Every event, toasted or not, bumps the ◆ badge. */
  onEvent?: (e: CrewEvent) => void;
  onStatus?: (s: 'subscribed' | 'reconnecting' | 'error') => void;
}

export interface PersonalHandlers {
  onChange: (ev: { table: string }) => void;
}

export interface Repository {
  // identity ---------------------------------------------------------------
  loginRoster(crewSlug: string): Promise<RosterEntry[]>;
  signIn(userKey: string, password: string): Promise<AuthSession>;
  setPassword(next: string): Promise<void>;
  reauth(current: string): Promise<boolean>;
  /**
   * Change the account's LOGIN address (docs/ACCOUNT-IDENTITY.md step 2).
   *
   * This is the auth identity, not `profiles.recovery_email` — which is being
   * retired precisely because having two addresses that mean the same thing is
   * how they drift apart.
   *
   * The caller re-authenticates first. `enable_confirmations` is false, so the
   * change lands immediately with no round trip to the old address: without the
   * password gate, a stolen session would be a stolen account.
   *
   * Throws `EMAIL_TAKEN` when Auth already has that address — one address, one
   * account, and that is a refusal the surface renders rather than a crash.
   */
  changeEmail(next: string): Promise<void>;
  /**
   * Email a password-reset link to `email` (docs/EMAIL.md).
   *
   * **RESOLVES WHETHER OR NOT THE ADDRESS HAS AN ACCOUNT, and that is the
   * contract, not an accident of the implementation.** A reset form that
   * answered "no such account" would be a free membership oracle for anybody
   * with a list of addresses — and the caller must therefore never render one,
   * which is why this returns `void` rather than a found/not-found verdict
   * there would be a temptation to show.
   *
   * It DOES throw on a transport failure or a rate-limit refusal, because those
   * are about the request rather than the account: telling somebody the mail is
   * on its way when the call never left the browser is the one lie worth
   * avoiding here.
   *
   * The link is built by GoTrue from the project's Site URL plus whatever
   * `redirectTo` the implementation passes, and lands back on the app as a
   * `RecoveryLink` fragment. Both ends are configuration nobody can set from
   * here: docs/STAGING.md §2 "URL Configuration".
   */
  sendPasswordReset(email: string): Promise<void>;
  /**
   * Install the session a recovery link carries, and say who it belongs to.
   *
   * Separate from `signIn` because there is no password involved: the token IS
   * the proof, and the caller's next move is `setPassword` on the session this
   * returns. Throws `LINK_EXPIRED` when the tokens are stale or malformed,
   * which is the common case — reset links are short-lived by design and people
   * open mail late.
   */
  resumeRecovery(link: RecoveryLink): Promise<AuthSession>;
  signOut(): Promise<void>;
  currentSession(): Promise<AuthSession | null>;
  onAuthChange(cb: (s: AuthSession | null) => void): Unsub;

  // crews (multi-crew phase C) ---------------------------------------------
  /** Every crew I belong to, name-ordered. Empty = the no-crew state. */
  myCrews(): Promise<CrewMembership[]>;
  /**
   * Repoint the SERVER-SIDE active crew (D2). Every subsequent read and every
   * RPC that stamps `crew_id := my_crew_id()` follows from this, which is why
   * `switchCrew` must drain in-flight writes BEFORE calling it (D10).
   */
  setActiveCrew(crewId: string): Promise<void>;
  /**
   * Start a crew. Two arms, matching `crew-create`'s own (D6):
   *
   *   without `account` — an already-signed-in account starting another crew.
   *   with `account`    — signed OUT: the account is created in the same
   *                       transaction as the crew, and the caller is its owner.
   *
   * The signed-out arm returns NO SESSION by design, so the caller signs in
   * with the password it just collected — `signIn` is the thing that installs
   * tokens correctly, and handing them back to be installed by hand would be
   * a worse version of it.
   */
  createCrew(input: CreateCrewInput): Promise<CrewMembership>;
  /** Owner only. `confirmName` must match the crew's name — checked here, in
   *  the edge function, and again inside the transaction (D6). */
  deleteCrew(crewId: string, confirmName: string): Promise<void>;
  /**
   * Rename a crew you own (migration 3030). Returns the name as stored, which
   * is the trimmed one — so the surface shows what landed rather than what was
   * typed.
   *
   * **THE SLUG DOES NOT CHANGE.** It is in every invitation link already sent
   * and in members' bookmarks, and `crew_slug_tombstones` exists so that a slug
   * can never come to mean a different crew. The slug is the crew's permanent
   * identity; the name is its label, and after a rename the two usually
   * disagree. That is intended, not drift.
   *
   * Throws `FORBIDDEN` for a crew you do not own — which is also the answer for
   * one that does not exist, so a uuid guesser learns nothing.
   */
  renameCrew(crewId: string, name: string): Promise<string>;
  /**
   * Hand the ACTIVE crew to another member (D4). Owner only, and irreversible
   * from the old owner's side — the moment it lands they are a player and
   * cannot take it back.
   *
   * Takes a `userKey` like the other member actions, not a uuid: the key is
   * what every surface holds, and the repository already owns the mapping.
   *
   * The database refuses a target who has not finished their first sign-in
   * (migration 3020): handing a crew to an account that cannot yet act as its
   * owner would strand it.
   */
  transferOwnership(userKey: string): Promise<void>;

  // boot -------------------------------------------------------------------
  fetchProfile(userId: string): Promise<Profile>;
  fetchCrewSnapshot(crewId: string): Promise<CrewSnapshot>;
  /**
   * The GOTY board for ONE year (AA phase 5, "one list per year"); the
   * interests half is year-less. The year read here is the year every later
   * board write goes to, until the next read — the store fetches before it
   * writes, and `saveBoards` diffs against what this last returned.
   */
  fetchMyBoards(userId: string, year?: number): Promise<MyBoards>;
  /** The years I have a GOTY list for in this crew, newest first. */
  fetchGotyYears(userId: string): Promise<number[]>;
  /**
   * A crewmate's GOTY lists in my active crew, newest year first (AA phase 5
   * §J; 3064 `crew_goty`). Read-only; `[]` for a member with no list.
   */
  fetchMemberGoty(userKey: string): Promise<MemberGotyYear[]>;
  /**
   * The boot fetch, BOUNDED.
   *
   * It used to be `from('v_games').select('*')`, which is fine at 24 rows and
   * fatal at thousands: past PostgREST's Max Rows that call silently returns the
   * 1000 LOWEST APPIDS, so a GOTY entry above the cut resolves to null in
   * `gById` and opens a blank screen — an error that never errors. Production
   * passed 490 rows the day the pool jobs landed.
   *
   * `appids` is anything the caller already knows it must be able to render.
   * Implementations return that, everything the crew's shared surfaces need, and
   * a bounded popularity head — never the whole table.
   */
  fetchCatalog(appids?: number[]): Promise<Game[]>;
  fetchSteamLink(userId: string): Promise<SteamLink | null>;

  // profile ----------------------------------------------------------------
  savePf(p: { displayName: string; recoveryEmail: string }): Promise<void>;
  onPickAv(dataUrl: string): Promise<{ avatarUrl: string }>;
  /**
   * The CHOOSE YOUR COLOR write (migration 3012). Tile pick: `(hue, null)` —
   * sets the hue and clears any custom color. Custom apply: `(null, hex)` —
   * sets the color, hue stands. Goes through the `set_profile_color` RPC, not
   * a column update, so multi-crew A1 can re-body it server-side.
   */
  setColor(hue: number | null, col: string | null): Promise<void>;
  /** `USE THE COLOR ✕` — drop the uploaded image so the color tile shows again.
   * Self-only, one press: nothing crew-wide is destroyed. */
  dropAvatar(): Promise<void>;

  // list / votes / comments ------------------------------------------------
  ensureGame(provisional: ProvisionalGame): Promise<number>;
  quickAdd(appid: GameId): Promise<ListEntry>;
  removeFromList(entryId: EntryId): Promise<void>;
  setVote(entryId: EntryId, value: VoteValue | null): Promise<void>;
  setIntAt(order: GameId[]): Promise<void>;
  postC(entryId: EntryId, body: string): Promise<Comment>;

  // media carousel ---------------------------------------------------------
  /**
   * Attach a crew video to `game`. Takes the whole `Game`, not an appid,
   * because a live-search result is not in `catalog_games` yet and the FK needs
   * a row — the implementation mints the provisional one through `ensure_game`,
   * exactly as `quickAdd` does.
   *
   * Throws `RepoError('DUPLICATE')` when the crew already has that clip on that
   * game, which the store turns into `✕ ALREADY ADDED`.
   */
  addGameVideo(game: Game, v: VideoDraft): Promise<GameVideo>;
  removeGameVideo(id: string): Promise<void>;

  // goty -------------------------------------------------------------------
  addPot(appid: GameId): Promise<void>;
  saveBoards(patch: Partial<MyBoards>): Promise<void>;
  /**
   * The member's most-played games of `year`, straight from Steam.
   *
   * Throws `RepoError` with the wire code (`NOT_LINKED`, `NO_KEY`, `UPSTREAM`,
   * `THROTTLED`) so the store can say which one happened. A private profile is
   * NOT a throw — it is `{profilePublic: false, games: []}`, matching the
   * function's own envelope (ARCHITECTURE §3.3).
   */
  steamFill(year: number): Promise<SteamFillResult>;
  /**
   * Where the caller's full library check stands, or null when there has never
   * been one. A plain row read — cheap enough to poll every few seconds while a
   * member watches `CHECKING YOUR STEAM LIBRARY · 140 OF 400`, and it asks Steam
   * nothing. Never throws: a progress line that cannot be read is simply not
   * shown, and the next `steamFill` reports the truth either way.
   */
  fetchSteamBackfill(): Promise<SteamBackfill | null>;

  // what's new -------------------------------------------------------------
  markReviewed(): Promise<void>;
  seenUsers(gameIds: GameId[]): Promise<void>;
  fetchEvents(sinceIso: string, limit: number): Promise<CrewEvent[]>;

  // playtests --------------------------------------------------------------
  ptDoCheck(url: string): Promise<PtCheckResult>;
  ptDoTrack(input: PtSteamInput): Promise<Playtest>;
  ptDoAddOff(input: PtOffInput): Promise<Playtest>;
  ptToggleInt(playtestId: string): Promise<boolean>;
  ptReport(playtestId: string, s: 'in' | 'req'): Promise<void>;
  ptClearSt(playtestId: string): Promise<void>;
  ptUntrack(playtestId: string): Promise<{ muted: boolean }>;
  ptBrowse(q: string): Promise<PtBrowseRow[]>;

  // steam link -------------------------------------------------------------
  steamLinkStart(returnTo: string): Promise<{ redirectUrl: string }>;
  steamUnlink(): Promise<void>;
  steamSyncNow(): Promise<SyncResult>;

  // admin ------------------------------------------------------------------
  /**
   * `role` is gone from the payload: a crew has exactly one owner and everybody
   * else is a player (D4), so there was never a second value to send. The edge
   * function has ignored the field since phase B and `op=role` is retired.
   */
  doAddUser(i: { name: string; email: string }): Promise<Invite>;
  doResetPw(userKey: string): Promise<Invite>;
  doRm(userKey: string): Promise<void>;

  // invitations (3026, docs/INVITES.md) ------------------------------------
  /**
   * Invite somebody by ADDRESS ALONE. No display name — they choose their own —
   * and no temp password, because no account is created until they accept.
   *
   * Supersedes RESEND too: re-inviting the same address mints a new link and
   * kills the previous one, which is why there is no separate resend method.
   * The old token is stored only as a hash and can never be shown again.
   */
  inviteMember(email: string): Promise<InviteSent>;
  /** Open invitations for the active crew. Never carries a token. */
  listInvites(): Promise<PendingInvite[]>;
  /** Cancel one, keyed on the address — at most one can be open per address. */
  revokeInvite(email: string): Promise<void>;

  // joining, from the invitee's side — no session required -----------------
  /** Read an invitation without consuming it. Safe to call repeatedly. */
  peekInvite(token: string): Promise<InvitePeek>;
  /**
   * Accept it. Signed in, both fields are ignored and the account is untouched;
   * signed out, they are required and become the new account.
   */
  acceptInvite(i: {
    token: string;
    displayName?: string;
    password?: string;
  }): Promise<InviteAccepted>;
  /** Decline it. Terminal — a declined invitation cannot be revived, because
   *  the person who declined would never learn that somebody else later used
   *  the same forwarded link. */
  declineInvite(token: string): Promise<void>;

  // join links (3056, docs/JOIN-LINKS.md) ----------------------------------
  // THE LEADER'S SIDE. Everything here needs a session and a party they own.
  /** Is there a live link, and when was it minted. Never a token. */
  partyJoinLink(): Promise<JoinLinkState>;
  /** Mint or rotate. Returns the URL ONCE — rotating kills the previous link,
   *  which the surface has to say out loud, because the old token was never
   *  stored and cannot be shown or revived. */
  newPartyJoinLink(): Promise<JoinLinkMinted>;
  /** Turn it off. Does NOT touch requests already made: those are people who
   *  asked while the door was open. */
  offPartyJoinLink(): Promise<void>;
  /** Open requests for the active party, newest first. */
  listJoinRequests(): Promise<JoinRequest[]>;
  /** Approve or deny one. Approving is what creates the membership. */
  decideJoinRequest(id: string, decision: 'approve' | 'deny'): Promise<void>;

  // asking, from the visitor's side — no session required for the first three
  /** Read a join link without spending anything. Safe on a reload. */
  peekJoinLink(token: string): Promise<JoinLinkPeek>;
  /**
   * Mail a six-digit code to prove an address. Creates NO account and NO
   * request — nothing a person typed is persisted before their address is
   * proven, so an abandoned attempt leaves an address and a dead code.
   */
  startJoinCode(token: string, email: string): Promise<JoinCodeSent>;
  /** Spend the code, create the account, record the ask. */
  verifyJoinCode(i: {
    token: string;
    email: string;
    code: string;
    displayName: string;
    password: string;
  }): Promise<JoinAsked>;
  /** Ask, as somebody who already has an account. No code — existing accounts
   *  are verified by decision (docs/JOIN-LINKS.md §3.3). */
  requestJoin(token: string): Promise<JoinAsked>;
  /** Do I have a live request? The crewless boot calls this, so a rejection
   *  must fall through to the old behaviour rather than hang. */
  joinRequestStatus(): Promise<JoinStatus>;
  /** Withdraw my own. An ask you can only wait on is a notification. */
  withdrawJoinRequest(): Promise<void>;

  // steam facts (SWR) ------------------------------------------------------
  fetchLive(q: string, signal?: AbortSignal): Promise<LiveSearchResult>;
  /**
   * P6 F-1: the §6.2 CheapShark fallback, behind `GET /api/cs/search?q=`.
   *
   * Raw CheapShark rows, unrewritten — `store/slices/ui.ts` owns the row →
   * `Game` transcription (`toCsGame`/`mapCheapShark`, prototype line 1645) and
   * `worker/src/cheapshark.ts` deliberately passes the payload through so that
   * mapping does not fork. Lives on the repository rather than in the slice
   * because `apiBase`/`fetchImpl` (and the §4.3 envelope) live here — this was
   * the last third-party origin the browser touched.
   *
   * Throws `RepoError` on any envelope failure, `stale` included.
   */
  fetchCsRows(q: string, signal?: AbortSignal): Promise<unknown[]>;
  /**
   * One page of Steam's own search — `GET /api/steam/browse`.
   *
   * Distinct from `fetchLive`, which is the 10-row typeahead over `storesearch`.
   * This one pages 50 at a time across ~272,000 games and can filter by tag,
   * which is what makes Discover a place to browse rather than a lookup box
   * (docs/DISCOVERY.md §2.2 records why no other endpoint can do it).
   *
   * Implementations MUST register every returned row where `quickAdd` can find
   * it. Adding a browse result to Games Tonight inserts into `list_entries`,
   * whose FK the games table has never heard of — the retry mints the row from
   * whatever the repository has cached, so a row that was never registered
   * becomes a permanent `Steam App #<appid>` with no name and no art.
   */
  fetchBrowse(q: BrowseQuery, signal?: AbortSignal): Promise<BrowsePageResult>;
  /**
   * Seat counts for browsed appids the rendered rows could not answer — the
   * live-once lookup (`player-lookup`, migration 3034). Asks about ≤50 appids
   * a call; the function answers from `game_players` where it can, asks IGDB
   * once for the never-asked remainder, caches what it learns, and the
   * nightly systems own the row thereafter (90-day re-verify, PCGW top-up).
   *
   * FAIL-SOFT BY CONTRACT: implementations answer `[]` on any failure —
   * offline, unauthenticated, function not yet deployed — because this is an
   * enrichment of a page already on screen, and a browse must never surface
   * an error over it. The caller treats absence as "still unknown", which is
   * already a state every row renders honestly.
   */
  lookupSeats(appids: number[]): Promise<SeatCount[]>;
  /** Steam's tag vocabulary for the picker — `GET /api/steam/tags`, 446 rows. */
  fetchTagList(): Promise<SteamTag[]>;
  /**
   * The 90-day CCU series for one appid, for the Detail chart.
   *
   * Keyed by **appid, not game id**, so it keeps working for a live-search row
   * that has no `catalog_games` row of its own yet — the samples are about the
   * Steam app, not about our record of it.
   *
   * `sample` marks a series the backend did not measure. Only `LocalRepository`
   * sets it, and it is what keeps the `SAMPLE` chip on the demo chart while
   * guaranteeing production can never draw an unlabelled fabrication.
   */
  fetchCcuSeries(appid: number): Promise<{ points: CcuSample[]; sample: boolean }>;
  /**
   * The 12-month price series for one appid, for the Detail band's sparkline
   * and its LOW chip (migration 3045, docs/DETAIL-BAND.md lane C).
   *
   * Keyed by appid for the reason `fetchCcuSeries` is: the samples are about
   * the Steam app, not about our record of it.
   *
   * FAIL-SOFT, same contract and same reason: an unavailable sparkline is a
   * missing sparkline, and the Detail screen has to render either way. A throw
   * here would take a whole detail-open down for an enrichment.
   */
  fetchPriceSeries(appid: number): Promise<{ points: PriceSample[]; sample: boolean }>;
  /**
   * The Detail-only columns of `steam_facts` for one appid — the facts the band
   * renders that no tile needs (migration 3048; lane A's 30-day review columns
   * will join them here).
   *
   * WHY NOT WIDEN `discover_page`. These are Detail-only, and that RPC's row
   * JSON is paid for by every tile on every feed page. Carrying four more
   * columns per row so one screen can read them is the waste the card-field
   * registry exists to prevent (docs/DISCOVERY.md §5.14), and `discover_page`
   * is a function contract with its own promotion cost besides.
   *
   * FAIL-SOFT: `null` on any failure, which the caller renders as "not checked
   * yet" — the same state a row genuinely in that position renders.
   */
  fetchDetailFacts(appid: number): Promise<DetailFacts | null>;
  /**
   * Who in the crew owns ONE game — migration 3007's `crew_game_ownership`.
   *
   * `linked` and `owned` are separate on purpose and must stay that way at
   * every layer: a member who has not connected Steam is `linked:false`, and
   * collapsing that into `owned:false` would assert they do not own a game
   * nobody has asked them about. The roster renders `NO STEAM LINK` off
   * `linked`, never off `owned`.
   *
   * This is the ONLY cross-member read in the app. `steam_owned_apps` and
   * `steam_links` remain self-only under RLS; the RPC is a narrow carve-out
   * that answers one appid with booleans, so a library cannot be enumerated
   * through it. See PORTING-NOTES W-04 — it reverses P2-08 and migration
   * 2800's "a COUNT, never a name".
   */
  fetchCrewOwnership(appid: number): Promise<CrewOwnershipRow[]>;
  /**
   * The same question asked about MANY games at once — migration 3046's
   * `crew_games_ownership`, and the reader behind the OWNED BY filter.
   *
   * Returns OWNERS ONLY: `appid → the user keys that own it`, with an appid
   * absent from the map when nobody in the crew owns it. That is a real answer,
   * not a silence, because the CALLER NAMED THE APPIDS — everything asked about
   * is answered, so the store seeds `[]` for the whole request before merging.
   * Distinguishing "asked, nobody owns it" from "not asked" is the entire
   * contract: `passFlt` hides a row on the first and never on the second.
   *
   * `linked` is deliberately NOT part of this shape. A member who has not
   * connected Steam owns nothing here, and reporting that as `owned: false`
   * alongside real answers is the P2-08 fabrication in a new costume — the
   * panel reads `fetchCrewSteamLinks()` for the link fact and disables those
   * members instead of letting this read answer for them.
   *
   * SERVER-CAPPED at 1000 appids per call, with a raise rather than a silent
   * truncation; implementations chunk below that. Throws on failure — unlike
   * `fetchCrewOwnership`, this is not on a render path, and a cached empty
   * answer here would read as "nobody owns anything" and empty every board.
   */
  fetchCrewOwnedBy(appids: readonly number[]): Promise<Record<number, string[]>>;
  /**
   * MY OWN library rows for the NAMED appids — `appid → lifetime playtime in
   * minutes`, the BACKLOG's PLAYED / OWNED / UNOWNED tabs.
   *
   * NO RPC AND NO MIGRATION, and that is the whole reason this reads the table
   * directly: `steam_owned_apps` carries a self-only select policy and a
   * `select` grant to `authenticated` (migration 1000), so the rows this can
   * ever return are the caller's own. Same shape of call as `fetchPriceSeries`.
   * The crew-wide question next door needs `crew_games_ownership` precisely
   * BECAUSE that one reaches past the caller; this one must not, and a plain
   * select is what makes that structural rather than a promise.
   *
   * OWNED ROWS ONLY. An appid absent from the answer is one the member does not
   * own, and the store seeds every appid it asked about before merging, exactly
   * as `ensureOwnedBy` does — "asked, not owned" and "never asked" are
   * different, and only the first may put a game in UNOWNED.
   *
   * PLAYTIME IS LIFETIME, as Steam reports it; `0` is a real measurement (owned,
   * never launched) and is why the map's value may be zero rather than the entry
   * being dropped.
   *
   * THROWS on failure, like `fetchCrewOwnedBy` and for the same reason: an empty
   * answer is indistinguishable from "you own none of these", which would file
   * the member's whole shelf under UNOWNED and call it a filter result.
   */
  fetchMyLibrary(appids: readonly number[]): Promise<Record<number, number>>;
  /**
   * Who in the crew has linked Steam at all — migration 3010's
   * `crew_steam_links`. One row per ACTIVE member of the caller's own crew.
   *
   * THIS THROWS ON FAILURE, where `fetchCrewOwnership` answers `[]`, and the
   * difference is deliberate. That one is re-asked per appid from a render path,
   * so a cached empty answer is what stops it hammering the RPC; this one is
   * asked once per visit to the Friends screen, so a failure can be left
   * uncached and retried on the next visit. The store keeps `crewLinks` null
   * either way, and null renders as PENDING — never as `STEAM NOT LINKED`,
   * which is the fabrication deviation P2-08 was recorded for.
   */
  fetchCrewSteamLinks(): Promise<CrewLinkRow[]>;
  /**
   * ONE crewmate's MY INTERESTS ordering — migration 3059's
   * `crew_interest_order`, the appids in the order that member placed them.
   *
   * What it is for: Games Tonight filtered to a single member files every game
   * under THAT member's call, and until 3059 ordered the rows by a group
   * average that is a constant once the group is one person — so the tiers came
   * out in the order the crew added the games. This is the array that makes the
   * row read as the member's own board (`lib/logic.ts#sharedRowGames`).
   *
   * ONE MEMBER PER CALL, deliberately, and the store asks lazily: there is no
   * crew-wide sweep to answer a question the rail only ever asks about one
   * person. The GOTY board is NOT reachable through this — that one is self-only
   * and says so on screen.
   *
   * `[]` IS A REAL ANSWER — "they have ranked nothing" — and is NOT the same as
   * a failed read. THROWS on failure, like `fetchCrewSteamLinks` and for the
   * same reason: the caller leaves the key unanswered so the board keeps its
   * average sort, rather than caching an empty order that would look exactly
   * like a member who has never dragged anything.
   */
  fetchCrewInterestOrder(userKey: string): Promise<GameId[]>;

  // my games (AA phase 2; migration 3060) -----------------------------------
  /**
   * The signed-in account's backlog, newest last. Self-only: the `backlog`
   * table admits nobody else's rows, so there is no crew read to pair with it.
   */
  fetchBacklog(): Promise<BacklogEntry[]>;
  /** Shelve a game. A live-search row the catalog has never seen is minted first, as `quickAdd` does. */
  addBacklog(appid: GameId): Promise<void>;
  /**
   * Shelve many games in ONE write; ones already shelved are skipped, not an
   * error. Does NOT mint: every appid must already have a catalog row (the
   * wishlist import mints first), because one missing row fails the batch.
   */
  addBacklogMany(appids: GameId[]): Promise<void>;
  removeBacklog(appid: GameId): Promise<void>;
  /**
   * The signed-in member's Steam wishlist, named. Throws `RepoError` with the
   * wire code (`NOT_LINKED`, `UPSTREAM`); a private or empty wishlist is NOT a
   * throw — it is `{open: false}`.
   */
  steamWishlist(): Promise<SteamWishlistResult>;
  /**
   * Every score the reader may see: their own, plus the active members of
   * their active crew (the `game_scores_select` policy, 3060). One read for the
   * whole party because the notice board wants everybody's last 14 days at
   * once, and the roster is small.
   *
   * THROWS on failure, like `fetchCrewInterestOrder`: an empty answer is a real
   * answer ("nobody has scored anything") and must not be what a failed read
   * looks like.
   */
  fetchScores(): Promise<CrewScoreRow[]>;
  /**
   * Recommendations I am party to in the active crew — sent to me or by me
   * (AA phase 3; 3062). The read policy bounds it; the client filters nothing.
   */
  fetchSends(): Promise<SendRec[]>;
  /**
   * The sender's one door (3062 `recommend_set`). `edit = false` is a SEND:
   * each named recipient's earlier card for this game is replaced whole.
   * `edit = true` is an EDIT: unnamed recipients lose it, the ones who stay
   * keep their answer and get the new note, new ones get a fresh card; an
   * empty list is a REVOKE and the note is ignored. Recipients are user keys.
   */
  setSends(appid: GameId, to: string[], note: string, edit: boolean): Promise<void>;
  /** The recipient's door (3062 `recommend_answer`). */
  answerSend(id: string, st: SendStatus): Promise<void>;
  /** My watches, by game id (AA phase 4; 3063). Self-only. */
  fetchWatch(): Promise<Record<string, WatchRec>>;
  /**
   * Write one game's watch; `null` (or a record with nothing armed) deletes
   * it — a watch with no trigger cannot exist. A game the catalog has never
   * carried is minted on the way in, like a shelf row.
   */
  putWatch(appid: GameId, rec: WatchRec | null): Promise<void>;
  /**
   * The NOTICE BOARD's rows (3063 `board_events`): the party's scores and the
   * triggers I armed that fired, last 14 days, as the policy hands them back.
   */
  fetchBoard(): Promise<BoardEvent[]>;
  /** `null` clears — a cleared score is the row's absence, never a zero. */
  setScore(appid: GameId, score: number | null): Promise<void>;

  /** `null` when the game has no override — migration 3008's reader. */
  fetchPlayerOverride(appid: number): Promise<PlayerOverride | null>;
  /**
   * Set or clear the global player count. `min`/`max` both `null` CLEARS — the
   * RPC has no reset flag, and one-sided is `BAD_RANGE` rather than a clear.
   *
   * Rejects with a `SetPlayerCountError`. Resolves with the row's count AS THE
   * RESOLVER NOW SEES IT, which is why this returns a value at all: a SET's
   * outcome is predictable client-side because an override is the top rung, but
   * a CLEAR's is not — the rung underneath was never on the wire. Guessing would
   * leave the rail showing the number the member just deleted.
   */
  setPlayerCount(
    appid: number,
    min: number | null,
    max: number | null,
  ): Promise<ResolvedPlayers | null>;
  /**
   * One page of the ranked, personalised default view (`discover_page`).
   *
   * Ranking runs in Postgres and not here, because one of its operands is
   * `steam_owned_apps` — per-user data whose entire protection is a self-only
   * row policy. Shipping the crew's libraries to the browser to sort them there
   * is both slower and a privacy question worth not opening (DISCOVERY.md §5.4).
   *
   * Like `fetchBrowse`, implementations MUST register every returned row where
   * `quickAdd` can find it, or adding a feed row to Games Tonight mints a
   * permanent `Steam App #<appid>` in the crew-shared table.
   *
   * It ranks the games table ONLY. Merging live-search rows into the same grid
   * is the caller's job, or a game outside the nightly jobs' reach could never
   * be found.
   *
   * The feed is not its only caller: page 0 of a TERM browse asks it the same
   * `q` Steam is asked, and merges the answers ahead of Steam's rows
   * (`slices/browse.ts#fetchBrowse`). `discover_page` accepts `q` for exactly
   * this — it is the only door left for a delisted title, which Steam's own
   * search omits outright.
   */
  fetchDiscoverPage(q: DiscoverQuery, signal?: AbortSignal): Promise<DiscoverPageResult>;
  enrich(appid: number, light: boolean): Promise<EnrichResult>;
  giFlush(appids: number[]): Promise<AssetsResult>;
  saveFix(patches: SteamFactPatch[]): Promise<void>;

  // rounds (migration 3039, docs/ROUNDS.md) ---------------------------------
  /**
   * The shared clock, as epoch ms. Clients sample it a few times, keep the
   * lowest-RTT sample and derive `serverNow() = Date.now() + offset`; every
   * wheel pose is computed against THAT, never against the local clock.
   */
  serverNow(): Promise<number>;
  /** The crew's open round, or null (an idle round reads as null). */
  roundGet(): Promise<RoundSnapshot | null>;
  /**
   * Every write below is an RPC that enforces the round's rules server-side
   * (leader / owner / membership / quota / the shut rail) and returns the
   * round as it stands afterwards — `null` when it no longer exists (LEAVE as
   * the last one in, END). A refusal throws `RepoError` whose message is the
   * server's own reason, in lower case; the store upper-cases it for a toast.
   */
  roundStart(): Promise<RoundSnapshot | null>;
  roundJoin(): Promise<RoundSnapshot | null>;
  roundLeave(): Promise<RoundSnapshot | null>;
  /**
   * Leader only: take somebody else's seat back (2026-08-28). Never returns
   * `null` — the leader is still in by definition, so this can never be the
   * last one out the way LEAVE can.
   */
  roundKick(userKey: string): Promise<RoundSnapshot | null>;
  /** Toggle: spends a quarter on `appid`, or takes the caller's own back. */
  roundNominate(appid: number): Promise<RoundSnapshot | null>;
  roundClearMine(): Promise<RoundSnapshot | null>;
  roundSetWeighted(weighted: boolean): Promise<RoundSnapshot | null>;
  /** THE DRAW — leader only. The returned snapshot carries the spin record. */
  roundSpin(): Promise<RoundSnapshot | null>;
  roundLock(): Promise<RoundSnapshot | null>;
  roundUnlock(): Promise<RoundSnapshot | null>;
  roundUndoSpin(): Promise<RoundSnapshot | null>;
  roundReset(): Promise<RoundSnapshot | null>;
  roundEnd(): Promise<void>;

  // SCHEDULE (migration 3071, DESIGN-CHANGELOG 2026-10-14) ---------------------
  /**
   * Every night with anything on it between two night keys, inclusive. The
   * month grid asks for the weeks it draws; an empty night is absent.
   */
  schedGet(from: string, to: string): Promise<SchedMap>;
  /**
   * The writes. Each is an RPC that enforces the feature's rules server-side
   * (crew-list games only, once per night, only the nominator edits or
   * removes, the nominator's quarter stays on) and returns THE NIGHT as it
   * stands afterwards. A refusal throws `RepoError` with the server's reason.
   * `from` / `to` are hours of the night clock (9..24, 24 = LATE), null = open.
   */
  schedNominate(night: string, appid: number, from: number | null, to: number | null): Promise<SchedNom[]>;
  /** Toggle the caller's quarter on someone else's nomination. */
  schedBack(night: string, appid: number): Promise<SchedNom[]>;
  schedSetRange(night: string, appid: number, from: number | null, to: number | null): Promise<SchedNom[]>;
  schedRemove(night: string, appid: number): Promise<SchedNom[]>;
  /**
   * START GAME NIGHT from a night: the ordinary round, opened with `appids`
   * (the nominations the caller's clock says cross now) already in the hat,
   * every backer seated as a second. With a round already open it returns
   * that round, as `roundStart()` does.
   */
  schedStart(night: string, appids: number[]): Promise<RoundSnapshot | null>;

  // realtime ---------------------------------------------------------------
  subscribeCrew(crewId: string, h: CrewHandlers): Unsub;
  subscribePersonal(userId: string, h: PersonalHandlers): Unsub;
}

/**
 * Thrown by every repository method that fails in a way the UI must surface.
 * `code` is the Worker's `ErrCode` where one applies, or a repository-level
 * reason (`WRONG_PASSWORD`, `NOT_SIGNED_IN`, …).
 */
export class RepoError extends Error {
  readonly code: string;

  constructor(code: string, message?: string) {
    super(message ?? code);
    this.name = 'RepoError';
    this.code = code;
  }
}
